import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { useToast } from "../../hooks/use-toast";
import { cn } from "../../lib/utils";
import { Plus, Loader2, Search, Server, GitBranch, ShieldCheck, Users2, Trash2, Info, CheckCircle2, XCircle, MinusCircle, ChevronRight } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../ui/tooltip";

function AssetHealthDrillDownModal({ open, onClose, assetType, healthByType }) {
  const health = healthByType.find((h) => h.asset_type === assetType);
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid="compliance-asset-health-drilldown-modal">
        <DialogHeader>
          <DialogTitle>{assetType} checks</DialogTitle>
          <DialogDescription>
            {health ? `${health.total_pass}/${health.total_all} checks passing across all ${assetType} assets.` : "No check data for this asset type."}
            {" "}This is a type-level rollup, not a per-instance result — the source export has no per-instance identifier.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 max-h-80 overflow-y-auto">
          {health?.checks?.map((c) => {
            const allPass = c.fail === 0 && c.other === 0;
            const hasFail = c.fail > 0;
            return (
              <div
                key={c.monitor_type}
                className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2"
                data-testid={`compliance-asset-health-check-row-${c.monitor_type}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {allPass ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  ) : hasFail ? (
                    <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                  ) : (
                    <MinusCircle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                  )}
                  <span className="text-[12px] truncate">{c.label}</span>
                </div>
                <span className="text-[11px] font-medium text-muted-foreground shrink-0">
                  {c.pass}/{c.total} passing{c.fail ? ` · ${c.fail} failing` : ""}
                </span>
              </div>
            );
          })}
          {(!health || !health.checks?.length) && (
            <div className="text-[11.5px] text-muted-foreground text-center py-6">No checks tracked for this type.</div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="compliance-asset-health-drilldown-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const CATEGORY_ICON = {
  cloud_infra: Server,
  repository: GitBranch,
  critical_access_system: ShieldCheck,
  employee_group: Users2,
};

function AddAssetModal({ open, onClose, onSaved, categories }) {
  const { toast } = useToast();
  const [category, setCategory] = useState("cloud_infra");
  const [name, setName] = useState("");
  const [assetType, setAssetType] = useState("");
  const [provider, setProvider] = useState("");
  const [owner, setOwner] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast({ title: "Name is required", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await api.post("/compliance/assets", { category, name, asset_type: assetType || null, provider: provider || null, owner: owner || null });
      toast({ title: "Asset added" });
      setName(""); setAssetType(""); setProvider(""); setOwner("");
      onSaved?.();
    } catch (e) {
      toast({ title: "Couldn't add asset", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="compliance-asset-add-modal">
        <DialogHeader>
          <DialogTitle>Add asset</DialogTitle>
          <DialogDescription>Track a cloud resource, repository, critical access system, or access group.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="mt-1" data-testid="compliance-asset-add-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" autoFocus data-testid="compliance-asset-add-name-input" />
          </div>
          <div>
            <Label>Type</Label>
            <Input value={assetType} onChange={(e) => setAssetType(e.target.value)} className="mt-1" placeholder="e.g. AWS_EC2, BITBUCKET" data-testid="compliance-asset-add-type-input" />
          </div>
          <div>
            <Label>Provider</Label>
            <Input value={provider} onChange={(e) => setProvider(e.target.value)} className="mt-1" placeholder="e.g. AWS, AZURE" data-testid="compliance-asset-add-provider-input" />
          </div>
          <div>
            <Label>Owner</Label>
            <Input value={owner} onChange={(e) => setOwner(e.target.value)} className="mt-1" data-testid="compliance-asset-add-owner-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="compliance-asset-add-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy} className="gap-1.5" data-testid="compliance-asset-add-save-btn">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {busy ? "Adding…" : "Add asset"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ComplianceAssets({ isAdmin }) {
  const { toast } = useToast();
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [deletingId, setDeletingId] = useState(null);
  const [infraHealthPct, setInfraHealthPct] = useState(null);
  const [healthByType, setHealthByType] = useState([]);
  const [drilldownType, setDrilldownType] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: summary }, { data: list }, { data: health }] = await Promise.all([
        api.get("/compliance/assets/summary"),
        api.get("/compliance/assets"),
        api.get("/compliance/assets/infra-health"),
      ]);
      setCategories(summary.categories || []);
      setAssets(list.assets || []);
      setInfraHealthPct(health.overall_pct ?? null);
      setHealthByType(health.by_type || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const removeAsset = async (asset) => {
    setDeletingId(asset.id);
    try {
      await api.delete(`/compliance/assets/${asset.id}`);
      setAssets((prev) => prev.filter((a) => a.id !== asset.id));
      setCategories((prev) => prev.map((c) => (c.key === asset.category ? { ...c, count: c.count - 1 } : c)));
    } catch (e) {
      toast({ title: "Couldn't delete asset", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setDeletingId(null); }
  };

  if (loading) return <div className="h-64 rounded-xl bg-muted/40 animate-pulse" />;

  const filtered = assets.filter((a) => {
    if (categoryFilter !== "all" && a.category !== categoryFilter) return false;
    if (search && !`${a.name} ${a.identifier || ""} ${a.provider || ""} ${a.owner || ""}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-4" data-testid="compliance-assets-page">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {categories.map((c) => {
          const Icon = CATEGORY_ICON[c.key] || Server;
          const active = categoryFilter === c.key;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setCategoryFilter(active ? "all" : c.key)}
              className={cn(
                "text-left rounded-xl border p-3 transition-colors",
                active ? "border-primary bg-primary/10" : "border-border bg-card hover:border-primary/40"
              )}
              data-testid={`compliance-asset-category-card-${c.key}`}
            >
              <Icon className="w-4 h-4 text-muted-foreground mb-1.5" />
              <div className="text-xl font-bold">{c.count}</div>
              <div className="text-[11px] text-muted-foreground">{c.label}</div>
              {c.key === "cloud_infra" && infraHealthPct != null && (
                <div
                  className={cn(
                    "mt-1 text-[10px] font-semibold",
                    infraHealthPct >= 90 ? "text-emerald-600" : infraHealthPct >= 50 ? "text-amber-600" : "text-red-600"
                  )}
                  data-testid="compliance-asset-infra-health-overall"
                >
                  {infraHealthPct}% checks passing
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search assets…" className="pl-8 h-9" data-testid="compliance-assets-search" />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="h-9 w-[200px]" data-testid="compliance-assets-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {isAdmin && (
          <Button size="sm" className="gap-1.5 ml-auto" onClick={() => setModalOpen(true)} data-testid="compliance-asset-add-btn">
            <Plus className="w-3.5 h-3.5" /> Add asset
          </Button>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card divide-y divide-border">
        {filtered.map((a) => {
          const Icon = CATEGORY_ICON[a.category] || Server;
          const detail = a.identifier || a.owner || a.status || "—";
          return (
            <div key={a.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`compliance-asset-row-${a.id}`}>
              <div className="flex items-center gap-2.5 min-w-0">
                <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <div className="text-[12.5px] font-medium truncate">{a.name}</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {a.asset_type ? `${a.asset_type} · ` : ""}{detail}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {a.category === "cloud_infra" && a.health_pct != null && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => setDrilldownType(a.asset_type)}
                          className={cn(
                            "inline-flex items-center gap-1 text-[10px] font-semibold rounded-full border px-2 py-0.5 hover:opacity-80 transition-opacity",
                            a.health_pct >= 90 ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600" :
                            a.health_pct >= 50 ? "border-amber-500/40 bg-amber-500/10 text-amber-600" :
                            "border-red-500/40 bg-red-500/10 text-red-600"
                          )}
                          data-testid={`compliance-asset-health-badge-${a.id}`}
                        >
                          <Info className="w-2.5 h-2.5" /> {a.health_pct}% <ChevronRight className="w-2.5 h-2.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{a.health_label}</p>
                        <p className="text-primary-foreground/70">Type-level rollup across all {a.asset_type} assets — click to see which checks are failing.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {a.provider && <span className="text-[10px] font-semibold uppercase rounded-full border border-border px-2 py-0.5 text-muted-foreground">{a.provider}</span>}
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => removeAsset(a)}
                    disabled={deletingId === a.id}
                    className="text-muted-foreground hover:text-red-500 transition-colors"
                    data-testid={`compliance-asset-delete-btn-${a.id}`}
                  >
                    {deletingId === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {assets.length === 0 && <div className="text-[11.5px] text-muted-foreground text-center py-8">No assets tracked yet</div>}
        {assets.length > 0 && filtered.length === 0 && <div className="text-[11.5px] text-muted-foreground text-center py-8" data-testid="compliance-assets-empty">No assets match.</div>}
      </div>
      <AddAssetModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={() => { setModalOpen(false); load(); }} categories={categories} />
      <AssetHealthDrillDownModal open={!!drilldownType} onClose={() => setDrilldownType(null)} assetType={drilldownType} healthByType={healthByType} />
    </div>
  );
}
